/** How long the UI waits for triggered step-test runs before giving up. */
export const STEP_TEST_TIMEOUT_MS = 120_000;

export type StepFailure = {
  stepDir: string;
  timedOut: boolean;
  // Empty when the run errored before producing any spec results (or timed
  // out); otherwise the specs that failed.
  specs: { name: string; message: string | null }[];
};

export type TestExecution = {
  id: string;
  status: string;
  testSpecs: readonly {
    name: string;
    status: string;
    message: string | null;
  }[];
};

export type StepRunStatus = "running" | "passed" | "failed";

/**
 * Classify a single triggered step-test run. It's running until its execution
 * reaches a terminal status; once terminal it failed if any spec failed or the
 * execution itself errored, otherwise it passed.
 */
export function stepRunStatus(
  run: { executionId: string },
  byId: Map<string, TestExecution>
): StepRunStatus {
  const exec = byId.get(run.executionId);
  if (!exec || exec.status === "pending") return "running";
  const anyFailed = exec.testSpecs.some((s) => s.status === "failed");
  return anyFailed || exec.status === "error" ? "failed" : "passed";
}

/**
 * Decide which triggered step-test runs count as failures. A run fails if any
 * spec failed, if the execution itself errored, or — once we've timed out
 * waiting — if it never finished.
 */
export function computeStepTestFailures(
  runs: { stepDir: string; executionId: string }[],
  byId: Map<string, TestExecution>,
  timedOut: boolean
): StepFailure[] {
  const out: StepFailure[] = [];
  for (const run of runs) {
    const status = stepRunStatus(run, byId);
    if (status === "passed") continue;
    if (status === "running") {
      if (timedOut)
        out.push({ stepDir: run.stepDir, timedOut: true, specs: [] });
      continue;
    }
    const specs =
      byId
        .get(run.executionId)
        ?.testSpecs.filter((s) => s.status === "failed")
        .map((s) => ({ name: s.name, message: s.message })) ?? [];
    out.push({ stepDir: run.stepDir, timedOut: false, specs });
  }
  return out;
}
